// OrganicAssets_AutoFolderCreation_Spec §8.1. Pure helper — Drive filename →
// default AssetLabel text for a Local / Organic folder file.
//
// Rules (case-insensitive on the extension):
//   image extension → "Image - {Local|Organic} - "
//   video extension → "Video - {Local|Organic} - "
//   unknown / none  → "" (no default; the AM types a label from scratch)
//
// Applied once, at label-creation time, via the DriveAssetGrid stub pattern
// (same mechanism Stage 1 uses for adType). Never retroactive: a file that
// already carries a label entry (even an empty one the AM cleared) is left
// alone — see spec §8.2. The image set is the Stage 1 list expanded with
// HEIC / HEIF / AVIF per §8.1.

export type AssetFolderKind = 'local' | 'organic';

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'heic',
  'heif',
  'avif',
]);
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'avi', 'webm', 'm4v', 'mkv', 'flv']);

const KIND_LABEL: Record<AssetFolderKind, string> = {
  local: 'Local',
  organic: 'Organic',
};

// Lowercased extension after the final dot, or '' when there's no real
// extension (no dot, or a trailing dot).
export function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  if (dot < 0 || dot === fileName.length - 1) return '';
  return fileName.slice(dot + 1).toLowerCase();
}

export function isImageExtension(fileName: string): boolean {
  return IMAGE_EXTENSIONS.has(extensionOf(fileName));
}

export function isVideoExtension(fileName: string): boolean {
  return VIDEO_EXTENSIONS.has(extensionOf(fileName));
}

export function defaultAssetLabel(fileName: string, kind: AssetFolderKind): string {
  const ext = extensionOf(fileName);
  if (IMAGE_EXTENSIONS.has(ext)) return `Image - ${KIND_LABEL[kind]} - `;
  if (VIDEO_EXTENSIONS.has(ext)) return `Video - ${KIND_LABEL[kind]} - `;
  return '';
}
